/**
 * 图片管理工具类
 * 负责处理图片固定、识别和状态管理
 */

export class ImageManager {
  /**
   * 检查图片是否为用户上传的图片
   * @param {string} imageUrl - 图片URL
   * @returns {boolean} 是否为用户上传图片
   */
  static isUserUploadedImage(imageUrl) {
    if (!imageUrl) return false
    
    return (
      imageUrl.startsWith('data:image/') || 
      imageUrl.startsWith('blob:') ||
      imageUrl.includes('localhost') ||
      imageUrl.includes('file://') ||
      imageUrl.startsWith('file:///') ||
      imageUrl.includes('image-') // 本地云存储的图片文件名模式
    )
  }

  /**
   * 检查图片是否为AI生成的图片
   * @param {string} imageUrl - 图片URL
   * @returns {boolean} 是否为AI生成图片
   */
  static isAIGeneratedImage(imageUrl) {
    if (!imageUrl) return false
    
    // 检查常见的AI图片生成服务URL模式
    const aiImagePatterns = [
      /picsum\.photos/, // 模拟图片服务
      /unsplash\.com/, // Unsplash
      /pexels\.com/, // Pexels
      /api\.openai\.com/, // OpenAI API
      /api\.replicate\.com/, // Replicate API
      /api\.stability\.ai/, // Stability AI
      /api\.midjourney\.com/, // Midjourney API
      /doubao\.volcengineapi\.com/, // 豆包API
      /volcengineapi\.com/, // 火山引擎API
      /seedream\.com/, // Seedream API
      /api\.doubao\.com/, // 豆包API
    ]
    
    return aiImagePatterns.some(pattern => pattern.test(imageUrl))
  }

  /**
   * 检查图片是否应该被固定（不被系统替换）
   * @param {string} imageUrl - 图片URL
   * @param {boolean} imageFixed - 图片固定标记
   * @param {string} imageSource - 图片来源
   * @returns {boolean} 是否应该固定图片
   */
  static shouldFixImage(imageUrl, imageFixed = false, imageSource = 'none') {
    if (!imageUrl) return false
    
    // 如果明确标记为固定，则固定
    if (imageFixed) return true
    
    // 如果是用户上传的图片，则固定
    if (this.isUserUploadedImage(imageUrl)) return true
    
    // 如果是AI生成的图片且来源明确，则固定
    if (imageSource === 'ai' && this.isAIGeneratedImage(imageUrl)) return true
    
    return false
  }

  /**
   * 获取图片的固定状态和来源信息
   * @param {string} imageUrl - 图片URL
   * @param {boolean} existingFixed - 现有的固定状态
   * @param {string} existingSource - 现有的图片来源
   * @returns {object} 图片状态信息
   */
  static getImageStatus(imageUrl, existingFixed = false, existingSource = 'none') {
    if (!imageUrl) {
      return {
        imageFixed: false,
        imageSource: 'none',
        shouldPreserve: false
      }
    }

    // 如果已经有固定状态，保持现有状态
    if (existingFixed) {
      return {
        imageFixed: true,
        imageSource: existingSource,
        shouldPreserve: true
      }
    }

    // 判断图片来源和固定状态
    let imageSource = 'none'
    let imageFixed = false

    if (this.isUserUploadedImage(imageUrl)) {
      imageSource = 'user'
      imageFixed = true
    } else if (this.isAIGeneratedImage(imageUrl)) {
      imageSource = 'ai'
      imageFixed = true
    } else {
      imageSource = 'system'
      imageFixed = false
    }

    return {
      imageFixed,
      imageSource,
      shouldPreserve: imageFixed
    }
  }

  /**
   * 更新节点图片信息，保持固定状态
   * @param {object} node - 节点对象
   * @param {string} newImageUrl - 新图片URL
   * @param {string} source - 图片来源类型
   * @returns {object} 更新后的节点
   */
  static updateNodeImage(node, newImageUrl, source = 'ai') {
    if (!node || !newImageUrl) return node

    const imageStatus = this.getImageStatus(
      newImageUrl, 
      node.data?.imageFixed, 
      node.data?.imageSource
    )

    return {
      ...node,
      data: {
        ...node.data,
        sceneImage: newImageUrl,
        imageFixed: imageStatus.imageFixed,
        imageSource: imageStatus.imageSource
      }
    }
  }

  /**
   * 检查场景是否应该跳过图片生成
   * @param {object} scene - 场景对象
   * @returns {boolean} 是否应该跳过图片生成
   */
  static shouldSkipImageGeneration(scene) {
    if (!scene) return false

    // 检查是否有固定图片
    const imageStatus = this.getImageStatus(
      scene.sceneImage,
      scene.imageFixed,
      scene.imageSource
    )

    return imageStatus.shouldPreserve
  }

  /**
   * 为场景设置固定图片
   * @param {object} scene - 场景对象
   * @param {string} imageUrl - 图片URL
   * @param {string} source - 图片来源
   * @returns {object} 更新后的场景
   */
  static setFixedImage(scene, imageUrl, source = 'user') {
    if (!scene || !imageUrl) return scene

    const imageStatus = this.getImageStatus(imageUrl, false, source)

    return {
      ...scene,
      sceneImage: imageUrl,
      imageFixed: imageStatus.imageFixed,
      imageSource: imageStatus.imageSource
    }
  }
}

export default ImageManager

